import type { Operation, Difficulty } from '../types';

interface Problem {
  operation: Operation;
  num1: number;
  num2: number;
  expectedAnswer: number;
}

class ProblemGenerator {
  private lastProblem: Problem | null = null;
  private usedProblems: Set<string> = new Set();
  private operationWeights: Map<Operation, number> = new Map();

  // Zakresy liczb dla różnych poziomów trudności
  private readonly RANGES = {
    easy: {
      addition: { min: 1, max: 10 },
      subtraction: { min: 1, max: 10 },
      multiplication: { min: 1, max: 5 },
      division: {
        divisors: [2, 3, 4, 5],
        maxResult: 5
      }
    },
    medium: {
      addition: { min: 1, max: 100 },
      subtraction: { min: 1, max: 100 },
      multiplication: { min: 2, max: 10 },
      division: {
        divisors: [2, 3, 4, 5, 6, 7, 8, 9, 10],
        maxResult: 10
      }
    },
    hard: {
      addition: { min: 1, max: 1000 },
      subtraction: { min: 1, max: 1000 },
      multiplication: { min: 5, max: 20 },
      division: {
        divisors: Array.from({ length: 19 }, (_, i) => i + 2), // 2-20
        maxResult: 20
      }
    }
  };

  // Generuje klucz dla problemu do śledzenia powtórzeń
  private getProblemKey(problem: Problem): string {
    return `${problem.operation}-${problem.num1}-${problem.num2}`;
  }

  // Sprawdza czy problem był niedawno użyty
  private wasRecentlyUsed(problem: Problem): boolean {
    return this.usedProblems.has(this.getProblemKey(problem));
  }

  // Dodaje problem do historii użytych
  private addToHistory(problem: Problem): void {
    const key = this.getProblemKey(problem);
    this.usedProblems.add(key);
    
    // Zachowuj tylko ostatnie 20 problemów
    if (this.usedProblems.size > 20) {
      const [firstKey] = this.usedProblems;
      this.usedProblems.delete(firstKey);
    }
  }

  // Generuje liczby dla dodawania
  private generateAddition(difficulty: Difficulty): Problem {
    const range = this.RANGES[difficulty].addition;
    const num1 = Math.floor(Math.random() * (range.max - range.min + 1)) + range.min;
    const num2 = Math.floor(Math.random() * (range.max - range.min + 1)) + range.min;
    
    return {
      operation: 'add',
      num1,
      num2,
      expectedAnswer: num1 + num2
    };
  }

  // Generuje liczby dla odejmowania (zawsze num1 > num2)
  private generateSubtraction(difficulty: Difficulty): Problem {
    const range = this.RANGES[difficulty].subtraction;
    const num2 = Math.floor(Math.random() * (range.max / 2)) + range.min;
    const num1 = num2 + Math.floor(Math.random() * (range.max - num2)) + 1;
    
    return {
      operation: 'subtract',
      num1,
      num2,
      expectedAnswer: num1 - num2
    };
  }

  // Generuje liczby dla mnożenia
  private generateMultiplication(difficulty: Difficulty): Problem {
    const range = this.RANGES[difficulty].multiplication;
    const num1 = Math.floor(Math.random() * (range.max - range.min + 1)) + range.min;
    const num2 = Math.floor(Math.random() * (range.max - range.min + 1)) + range.min;
    
    return {
      operation: 'multiply',
      num1,
      num2,
      expectedAnswer: num1 * num2
    };
  }

  // Generuje liczby dla dzielenia (zawsze z wynikiem całkowitym)
  private generateDivision(difficulty: Difficulty): Problem {
    const { divisors, maxResult } = this.RANGES[difficulty].division;
    const divisor = divisors[Math.floor(Math.random() * divisors.length)];
    const result = Math.floor(Math.random() * maxResult) + 1;
    const dividend = divisor * result;
    
    return {
      operation: 'divide',
      num1: dividend,
      num2: divisor,
      expectedAnswer: result
    };
  }

  // Sprawdza czy nowy problem jest podobny do poprzedniego
  private isSimilarToPrevious(problem: Problem): boolean {
    if (!this.lastProblem) return false;

    // Sprawdź powiązania między działaniami
    if (
      (problem.operation === 'multiply' && this.lastProblem.operation === 'divide') ||
      (problem.operation === 'divide' && this.lastProblem.operation === 'multiply')
    ) {
      return problem.num1 === this.lastProblem.num1 || 
             problem.num1 === this.lastProblem.num2 ||
             problem.num2 === this.lastProblem.num1 || 
             problem.num2 === this.lastProblem.num2;
    }

    if (
      (problem.operation === 'add' && this.lastProblem.operation === 'subtract') ||
      (problem.operation === 'subtract' && this.lastProblem.operation === 'add')
    ) {
      return problem.num1 === this.lastProblem.num1 || 
             problem.num1 === this.lastProblem.num2 ||
             problem.num2 === this.lastProblem.num1 || 
             problem.num2 === this.lastProblem.num2;
    }

    // Sprawdź powtórzenia tych samych liczb
    return problem.num1 === this.lastProblem.num1 && 
           problem.num2 === this.lastProblem.num2;
  }

  // Wybiera następną operację w trybie mieszanym
  private selectNextOperation(operations: Operation[]): Operation {
    // Aktualizuj wagi operacji
    operations.forEach(op => {
      if (!this.operationWeights.has(op)) {
        this.operationWeights.set(op, 1);
      }
    });

    // Oblicz sumę wag
    let totalWeight = 0;
    operations.forEach(op => {
      totalWeight += this.operationWeights.get(op) || 1;
    });

    // Wybierz operację na podstawie wag
    let random = Math.random() * totalWeight;
    for (const op of operations) {
      const weight = this.operationWeights.get(op) || 1;
      if (random <= weight) {
        return op;
      }
      random -= weight;
    }

    return operations[0];
  }

  // Generuje nowy problem
  generateProblem(difficulty: Difficulty, operations: Operation[], mixedMode: boolean): Problem {
    let problem: Problem;
    let attempts = 0;
    const maxAttempts = 10;

    do {
      const operation = mixedMode ? 
        this.selectNextOperation(operations) : 
        operations[0];

      switch (operation) {
        case 'add':
          problem = this.generateAddition(difficulty);
          break;
        case 'subtract':
          problem = this.generateSubtraction(difficulty);
          break;
        case 'multiply':
          problem = this.generateMultiplication(difficulty);
          break;
        case 'divide':
          problem = this.generateDivision(difficulty);
          break;
        default:
          throw new Error('Nieznana operacja');
      }

      attempts++;
    } while (
      attempts < maxAttempts && 
      (this.wasRecentlyUsed(problem) || this.isSimilarToPrevious(problem))
    );

    // Aktualizuj historię i wagi
    this.addToHistory(problem);
    this.lastProblem = problem;

    // Aktualizuj wagi operacji
    if (mixedMode) {
      operations.forEach(op => {
        if (op === problem.operation) {
          this.operationWeights.set(op, 1);
        } else {
          this.operationWeights.set(op, (this.operationWeights.get(op) || 1) + 0.5);
        }
      });
    }

    return problem;
  }

  // Resetuje stan generatora
  reset(): void {
    this.lastProblem = null;
    this.usedProblems.clear();
    this.operationWeights.clear();
  }
}

// Eksportuj singleton
export const problemGenerator = new ProblemGenerator();